import { call, put, takeEvery } from 'redux-saga/effects';
import { SweetAlert } from '../../../../components/sweetalert/SweetAlert';
import * as types from './reportConfigurationActionType';
import * as actions from './reportConfigurationActions';
import * as services from './reportConfigurationServices';


function* fetchReportConfigurationSaga(action) {
    try {
        // Call the API - request utility already returns response.data
        const response = yield call(services.getAllReportConfiguration, action.payload);
        if (!response) {
            throw new Error('No response received from API');
        }

        // Dispatch success action with the data
        yield put(actions.fetchReportConfigurationSuccess(response?.values));

    } catch (error) {
        let errorMessage = 'Failed to fetch Performance Dashboard Configurations';

        console.error('Performance Dashboard Configuration API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });

        // Dispatch failure action with error
        yield put(actions.fetchReportConfigurationFailure(errorMessage));
    }
}

function* fetchReportConfigurationByIdSaga(action) {
    try {
        const { ReportConfigurationID } = action.payload;
        // Call the API
        const response = yield call(services.getReportConfigurationById, ReportConfigurationID);
        // Dispatch success action with the values directly (not nested)
        yield put(actions.fetchReportConfigurationByIdSuccess(response?.values || response));
    } catch (error) {
        let errorMessage = 'Failed to fetch Performance Dashboard Configuration details';
        console.error('Fetch Performance Dashboard Configuration By ID API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });
        // Dispatch failure action with error
        yield put(actions.fetchReportConfigurationByIdFailure(errorMessage));
        // Show error notification
        SweetAlert.error({ text: errorMessage });
    }
}

function* saveReportConfigurationSaga(action) {
    try {
        // Call the API
        const response = yield call(services.saveReportConfiguration, action.payload);

        if (response?.success) {
            // Dispatch success action
            yield put(actions.saveReportConfigurationSuccess(response));
            SweetAlert.success({
                text: response?.resultMessage || 'Successfully Saved Performance Dashboard Configuration'
            });
            // Fetch updated Performance Dashboard Configurations after successful save
            yield put(actions.fetchReportConfigurationRequest());
        } else {
            const errorMessage = response?.errorMessage || 'Failed to save Performance Dashboard Configuration';
            yield put(actions.saveReportConfigurationFailure(errorMessage));
            SweetAlert.error({ text: errorMessage });
        }
    }
    catch (error) {
        const errorMessage =
            error.response?.data?.errorMessage ||
            error.message ||
            'Failed to save Performance Dashboard Configuration';
        console.error('Save Performance Dashboard Configuration API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });
        // Dispatch failure action with error
        yield put(actions.saveReportConfigurationFailure(errorMessage));
        SweetAlert.error({ text: errorMessage });
    }
}

function* activateDeactivateReportConfigurationSaga(action) {
    try {
        const { ReportConfigurationID, status, userId } = action.payload;

        // Call the API
        const response = yield call(services.activateDeactivateReportConfiguration, ReportConfigurationID, status, userId);

        if (response?.success) {
            yield put(actions.activateDeactivateReportConfigurationSuccess(response));
            SweetAlert.success({
                text: response?.resultMessage || `Performance Dashboard Configuration has been ${status ? 'activated' : 'deactivated'} successfully`
            });
            // Fetch updated Performance Dashboard Configurations list after successful activation/deactivation
            yield put(actions.fetchReportConfigurationRequest());
        } else {
            const errorMessage = response?.errorMessage || 'Failed to update Performance Dashboard Configuration status';
            yield put(actions.activateDeactivateReportConfigurationFailure(errorMessage));
            SweetAlert.error({ text: errorMessage });
        }

    } catch (error) {
        const errorMessage =
            error.response?.data?.errorMessage ||
            error.message ||
            'Failed to update Performance Dashboard Configuration status';

        console.error('Activate/Deactivate Performance Dashboard Configuration API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });

        // Dispatch failure action with error
        yield put(actions.activateDeactivateReportConfigurationFailure(errorMessage));
        SweetAlert.error({ text: errorMessage });
    }
}

function* fetchPowerBIRefreshStatusSaga() {
    try {
        const response = yield call(services.getPowerBIReportsRefresh);
        yield put(actions.fetchPowerBIRefreshStatusSuccess(response?.values));
    } catch (error) {
        console.error('PowerBI Refresh Status API Error:', error);
        yield put(actions.fetchPowerBIRefreshStatusFailure('Failed to fetch refresh status'));
    }
}

function* fetchCapacityDropdownSaga(action) {
    try {
        const response = yield call(services.getAllReportConfiguration, action.payload);
        
        // Transform the dropdown items to the expected format
        const dropdownItems = response?.values?.capacityTableDDList || [];
        const transformedData = dropdownItems.map(item => ({
            value: item.Id,
            label: item.Name,
            ...item
        }));
        
        yield put(actions.fetchCapacityDropdownSuccess(transformedData));
    } catch (error) {
        console.error('Capacity Dropdown API Error:', error);
        yield put(actions.fetchCapacityDropdownFailure('Failed to fetch capacity dropdown'));
    }
}

export default function* reportConfigurationSaga() {
    yield takeEvery(types.FETCH_REPORT_CONFIGURATION_REQUEST, fetchReportConfigurationSaga);
    yield takeEvery(types.SAVE_REPORT_CONFIGURATION_REQUEST, saveReportConfigurationSaga);
    yield takeEvery(types.FETCH_REPORT_CONFIGURATION_BY_ID_REQUEST, fetchReportConfigurationByIdSaga);
    yield takeEvery(types.ACTIVATE_DEACTIVATE_REPORT_CONFIGURATION_REQUEST, activateDeactivateReportConfigurationSaga);
    yield takeEvery(types.FETCH_POWERBI_REFRESH_STATUS_REQUEST, fetchPowerBIRefreshStatusSaga);
    yield takeEvery(types.FETCH_CAPACITY_DROPDOWN_REQUEST, fetchCapacityDropdownSaga);
}